import React, { useEffect, useState } from 'react';
import { fromJS } from 'immutable';
import classNames from 'classnames';
import { Fade } from 'react-awesome-reveal';
import { useTranslation } from 'react-i18next';
import { Block, Paragraph, View } from '@uncinc/react-drupal-core';
import { Image, usePrevious } from '@uncinc/uncinc-react-kitchen-sink';
import Truncate from 'react-truncate';

import { formatDate } from '../../../../helpers/dates';
import calculateReadingTime from '../../../../helpers/calculateReadingTime';

import {
  Breadcrumbs,
  Button,
  Container,
  KPAnswersTeasers,
  Share,
  WysiwygField,
} from '../../../../components';
import { FEEDBACK_TOOL_BLOCK_ID } from '../../../../config/constants';

import './style.scss';

const NodeKPAnswerPageFull = ({ entity, className }) => {
  const [shouldRenderView, setShouldRenderView] = useState(false);
  const created = entity.getIn(['created', 0, 'value']);
  const classes = classNames(className);

  useEffect(() => {
    window.scrollTo(0, 0);
  });

  const { t } = useTranslation();

  const publicationDate = formatDate(new Date(created), 'd MMMM yyyy');
  const question = entity.getIn(['field_kp_question_relationship']);
  const ctaTitle = entity.getIn(['field_cta_title', 0, 'value'], '');
  const ctaContent = entity.getIn(['field_cta_content', 0, 'value'], '');
  const ctaLinkUrl = entity.getIn(['field_link', 0, 'alias']) || entity.getIn(['field_link', 0, 'uri']) || '';
  const ctaLinkId = ctaLinkUrl.substring(1);
  const ctaLinkTitle = entity.getIn(['field_link', 0, 'title'], '');
  const answerType = entity.getIn(['field_type_of_answer', 0, 'entity', 'name', 0, 'value']).toLowerCase();
  const questionEntityId = entity.getIn(['field_kp_question_relationship', 0, 'target_id']);
  const entityId = entity.getIn(['nid', 0, 'value']);
  const entityType = entity.getIn(['type', 0, 'target_type']);
  const previousEntityId = usePrevious(entityId);
  const readingTime = entity.getIn(['field_reading_time'], 0);
  const showImage = entity.getIn(['field_show_teaser_image', 0, 'value']);
  const relatedAnswers = entity.getIn(['field_related_answers']).map(
    (entity) => entity.getIn(['entity']),
  );


  const breadcrumbs = [
    {
      title: 'Home',
      url: '/',
    },
    {
      title: question.getIn([0, 'entity', 'title', 0, 'value']),
      url: question.getIn([0, 'entity', 'paths', 'nl']),
    },
  ];

  const readingTypes = [
    'longread',
    'blog',
    'shortread',
  ];

  useEffect(() => {
    setShouldRenderView(false);
    if (entityId !== previousEntityId) {
      setTimeout(() => {
        setShouldRenderView(true);
      });
    }
  }, [entityId]);

  return (
    <article typeof="schema:WebPage" className={classes}>
      <Container className="KPAnswerPage__header">
        <Breadcrumbs includeCurrent customCrumbs={fromJS(breadcrumbs)} />
      </Container>
      <Container>
        <div className="KPAnswerPage row">
          <div className="KPAnswerPage__answer col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-0 col-xl-7">
            <Fade triggerOnce bottom distance="50px" duration={500}>
              <h1 className="KPAnswerPage__title page-header">{entity.getIn(['title', 0, 'value'])}</h1>
              {entity.getIn(['body', 0, 'value']) && (
                <WysiwygField
                  property="schema:text"
                  className="KPAnswerPage__body"
                  html={entity.getIn(['body', 0, 'value'], '')}
                />
              )}

              <div className="KPAnswerPage__metadata">
                {readingTypes.includes(answerType) && (
                  <>
                    <span className={`icon icon-${answerType}`} />
                    <span className="reading-time">
                      {t('reading_time')} {calculateReadingTime(readingTime)} min.
                    </span>
                  </>
                )}
              </div>

              {showImage ? (
                entity.getIn(['field_image']).size > 0 && (
                  <div className="KPAnswerPage__image">
                    <Image
                      src={{
                        768: entity.getIn(['field_image', 0, 'styles', 'basic_image'], ''),
                      }}
                      includeWebP
                    />
                  </div>
                )
              ) : ''}

            </Fade>

            {entity.getIn(['field_paragraphs']).size > 0 && (
              <div className="Paragraphs">
                {
                  entity.getIn(['field_paragraphs']).map((item, index) => {
                    const paragraphId = item.get('target_id');
                    const paragraph = item.getIn(['entity']);
                    return (
                      <Paragraph
                        key={paragraphId}
                        entity={paragraph}
                        id={paragraphId}
                        containerless
                        onAnswerPage
                      />
                    );
                  })
                }
              </div>
            )}
            <Block
              id={FEEDBACK_TOOL_BLOCK_ID}
              parentEntityType={entityType}
              parentEntityId={entityId}
            />
          </div>
          <div className="col-12 col-lg-4 offset-xl-1">
            <div className="KPAnswerPage__recentAnswers">
              {questionEntityId && entityId && shouldRenderView && (
                <View id="kp_recent_answers" display="block_1" args={[questionEntityId, entityId]} question={question} />
              )}
            </div>
          </div>
        </div>
      </Container>
      <Container>
        <div className="row">
          <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-0 col-xl-7">
            <div className="KPAnswerPage">
              <div className="KPAnswerPage__answerLower">
                {entity.getIn(['field_author']).size > 0 && (
                  <div className="KPAnswerPage__author__wrapper">
                    {entity.getIn(['field_author']).map((author, index) => {
                      const title = author.getIn(['entity', 'title', 0, 'value']);
                      const key = author.getIn(['target_id']);
                      return (
                        <div key={key} className="KPAnswerPage__author">
                          <div className="KPAnswerPage__author__image">
                            <Image
                              src={{
                                0: author.getIn(['entity', 'field_image', 0, 'styles', 'square'], ''),
                              }}
                              includeWebP
                            />
                          </div>
                          <div className="KPAnswerPage__author__content">
                            <p className="tiny">{t('author')}</p>
                            <h3>{title}</h3>
                            <p className="small">
                              <Truncate lines={3} ellipsis="..." trimWhitespace>
                                {author.getIn(['entity', 'body', 0, 'value'])}
                              </Truncate>
                            </p>
                          </div>
                        </div>
                      );
                    })}

                  </div>
                )}

                <Share title={t('kp.share')} />
              </div>
            </div>
          </div>
        </div>
      </Container>
      <Container>
        <div className="KPQuestionPage__linked-questions">
          <h2>{t('kp.relevant_answers')}</h2>
          {relatedAnswers.size ? (
            <KPAnswersTeasers entities={relatedAnswers} />
          ) : (
            <View id="kp_answers" display="block_2" args={[questionEntityId, entityId]} />
          )}
        </div>
      </Container>

      <Container>
        {ctaLinkUrl && (
          <div className="KPAnswerPage__cta">
            {ctaTitle && (
              <h2>{ctaTitle}</h2>
            )}
            {ctaContent && (
              <WysiwygField
                property="schema:text"
                className="KPAnswerPage__cta__body"
                html={ctaContent}
              />
            )}
            <Button id={ctaLinkId} primary withIcon buttonIcon href={ctaLinkUrl}>{ctaLinkTitle}</Button>
          </div>
        )}
      </Container>

    </article>
  );
};

export default NodeKPAnswerPageFull;
